import { useCallback } from 'react';
import { ParsedQuery } from 'query-string';
import { useLocation } from 'react-router-dom';
import { useFilters } from './useFilters';
import { filterEmptyFilters } from '../helpers/helpers';
import {
  deleteParticipant,
  getParticipantsRequest,
  IPostParticipantBody,
  IPostTeamMemberBody,
  patchParticipant,
  patchParticipantTeamMember,
  postAddMemberToParticipantsTeam,
  postParticipant,
} from '../services/surveys';

export const useParticipantsTeam = () => {
  const { search } = useLocation();
  const { currentFilters, applyFilters } = useFilters();

  const getAppliedFilters = useCallback(() => {
    const params = Object.fromEntries(new URLSearchParams(search));
    delete params.card;
    return Object.fromEntries(new URLSearchParams(params));
  }, [search]);

  const downloadParticipants = async (surveyId: string, overrideFilters: ParsedQuery = {}) => {
    const { members, ...filters } = currentFilters;
    if (!filters.perPage) {
      return await getParticipantsRequest(surveyId, {
        ...filters,
        ...overrideFilters,
        perPage: '7',
      });
    }
    return await getParticipantsRequest(surveyId, { ...filters, ...overrideFilters });
  };

  const applyManyPartiMembersTileFilter = (
    memberFilter: any,
    ids: any[],
    additionalFilters?: any
  ) => {
    const { members } = currentFilters;
    const membersObj = members ? JSON.parse(members as string) : {};
    const objReadyForStringify = ids.reduce(
      (resp: any, id: any) => {
        if (id) {
          const currentMembers = membersObj[id] ? membersObj[id] : {};
          const newMembers = { ...currentMembers, ...memberFilter };
          const filteredMembers = filterEmptyFilters(newMembers);
          return { ...resp, [id]: filteredMembers };
        }
      },
      { ...membersObj }
    );
    const stringifiedObject = JSON.stringify(objReadyForStringify);
    return applyFilters({ members: stringifiedObject, ...additionalFilters });
  };

  const applyPartiMembersTileFilter = (filterObj: any, id: any) => {
    const { members } = currentFilters;
    const membersObj = members ? JSON.parse(members as string) : {};
    const currentMembers = membersObj[id] ? membersObj[id] : {};
    const newMembers = { ...currentMembers, ...filterObj };
    const filteredMembers = filterEmptyFilters(newMembers);
    const objReadyForStringify = { ...membersObj, [id]: filteredMembers };
    const stringifiedObject = JSON.stringify(objReadyForStringify);
    return applyFilters({ members: stringifiedObject });
  };

  const addParticipantsTeamMember = async (
    body: IPostTeamMemberBody,
    surveyId: string,
    teamId: string
  ) => {
    return await postAddMemberToParticipantsTeam(body, surveyId, teamId);
  };

  const deleteParticipantsTeamMember = async (surveyId: string, participantId: string) => {
    return await deleteParticipant(surveyId, participantId);
  };

  const editParticipantsTeamMember = async (
    body: IPostTeamMemberBody,
    surveyId: string,
    teamId: string,
    memberId: string
  ) => {
    return await patchParticipantTeamMember(body, surveyId, teamId, memberId);
  };

  const submitParticipant = async (surveyId: string, data: IPostParticipantBody) => {
    return await postParticipant(surveyId, data);
  };

  const patchParticipantMember = async (
    surveyId: string,
    body: IPostParticipantBody,
    participantId: string
  ) => {
    return await patchParticipant(surveyId, body, participantId);
  };

  return {
    applyPartiMembersTileFilter,
    applyManyPartiMembersTileFilter,
    addParticipantsTeamMember,
    editParticipantsTeamMember,
    deleteParticipantsTeamMember,
    getAppliedFilters,
    downloadParticipants,
    submitParticipant,
    patchParticipantMember,
  };
};
